import * as React from 'react'

import {
  HorizontalAlignment,
  useSiteConfig,
  useTheme,
} from '@thg-commerce/enterprise-core'
import { CategoryItemCardPicture } from '@thg-commerce/enterprise-network/src/ApolloProvider/resolvers/Types/CategoryItemCardPicture'
import { GlobalSixItemCategoriesPicture } from '@thg-commerce/enterprise-network/src/ApolloProvider/resolvers/Types/GlobalSixItemCategoriesPicture'
import {
  MultiCategoryCardSet,
  MultiCategoryCardSetV2,
} from '@thg-commerce/enterprise-network/src/generated/graphql'
import {
  CategorySet,
  CategorySetItemProps,
  CategorySetProps,
} from '@thg-commerce/enterprise-widget-category-set'
import {
  MarginInterface,
  PaddingInterface,
} from '@thg-commerce/enterprise-widget-editorial/theme'
import { WidgetBackgroundColour } from '@thg-commerce/gravity-elements'
import { PictureProps } from '@thg-commerce/gravity-system'
import {
  margin,
  mq,
  padding,
  spacing,
  styled,
} from '@thg-commerce/gravity-theme'

import { BaseWidgetProps } from '../types'

interface MultiCategoryCardSetV2WithChildren
  extends Omit<MultiCategoryCardSetV2, 'banners'> {
  banners: Omit<CategoryItemCardPicture, 'id' | 'query'>[]
}

interface MultiCategoryCardSetWithChildren
  extends Omit<MultiCategoryCardSet, 'banners' | 'backgroundColour'> {
  banners: Omit<CategoryItemCardPicture, 'id' | 'query'>[]
  backgroundColour?: string
}

export type CategorySetWidgetRendererProps = BaseWidgetProps &
  (
    | Omit<GlobalSixItemCategoriesPicture, 'id' | 'query'>
    | Omit<MultiCategoryCardSetWithChildren, 'id' | 'query'>
    | Omit<MultiCategoryCardSetV2WithChildren, 'id' | 'query'>
  )

const StyledCategorySet = styled(CategorySet)<{
  gutterPadding: PaddingInterface
  gutterMargin: MarginInterface
  styleOverride?: {
    margin: number
    width?: string
  }
}>`
  max-width: ${(props) =>
    props.styleOverride?.width ?? props.theme.site.siteWidth};
  margin: ${spacing(1)} ${spacing(2)};

  ${(props) => mq(props.theme.breakpointUtils.map, 'md')} {
    margin: 0 auto;
    padding: ${spacing(2)} 0;
  }

  ${(props) => padding(props.gutterPadding)}

  ${(props) =>
    props.styleOverride ? `margin: 0;` : margin(props.gutterMargin)}
`

const validAlignment = (alignment?: string | undefined | null) => {
  if (alignment === 'centre' || !alignment) {
    return 'center' as HorizontalAlignment
  }
  return alignment as HorizontalAlignment
}

const buildSixItemCategory = (
  title: string | null | undefined,
  picture: PictureProps,
  cta: string | null | undefined,
  link: string | null | undefined,
  align: string | undefined,
  contentGutterPadding: PaddingInterface,
): CategorySetItemProps => ({
  ...(title && {
    titleProps: { title },
  }),
  picture,
  contentGutterPadding,
  buttonContent: cta || undefined,
  buttonLink: link || undefined,
  horizontalAlignment: validAlignment(align),
})

const renderGlobalSixItemCategories = (
  props: CategorySetWidgetRendererProps,
  theme: ReturnType<typeof useTheme>,
  enableStyleOverrideSixItemCategoryWidget?: boolean,
) => {
  const categorySetProps: CategorySetProps = {
    ...(props.title && {
      titleProps: {
        title: props.title,
      },
    }),
    isRecommendationsCategory: props.isRecommendationsCategory || undefined,
    horizontalAlignment: validAlignment(props.itemTitleAlign),
    gutterPadding: theme.widget.categorySet.sixItemCategory.gutterPadding,
    container: theme.widget.categorySet.sixItemCategory.container,
    gutterMargin: theme.widget.categorySet.sixItemCategory.gutterMargin,
    styleOverride: enableStyleOverrideSixItemCategoryWidget
      ? {
          margin: 1,
        }
      : undefined,
    items: [
      buildSixItemCategory(
        props.itemOneTitle,
        props.pictures.pictureOne,
        props.itemOneCTA,
        props.itemOneLink,
        props.itemTitleAlign,
        theme.widget.categorySet.sixItemCategory.contentGutterPadding,
      ),
      buildSixItemCategory(
        props.itemTwoTitle,
        props.pictures.pictureTwo,
        props.itemTwoCTA,
        props.itemTwoLink,
        props.itemTitleAlign,
        theme.widget.categorySet.sixItemCategory.contentGutterPadding,
      ),
      buildSixItemCategory(
        props.itemThreeTitle,
        props.pictures.pictureThree,
        props.itemThreeCTA,
        props.itemThreeLink,
        props.itemTitleAlign,
        theme.widget.categorySet.sixItemCategory.contentGutterPadding,
      ),
      buildSixItemCategory(
        props.itemFourTitle,
        props.pictures.pictureFour,
        props.itemFourCTA,
        props.itemFourLink,
        props.itemTitleAlign,
        theme.widget.categorySet.sixItemCategory.contentGutterPadding,
      ),
      buildSixItemCategory(
        props.itemFiveTitle,
        props.pictures.pictureFive,
        props.itemFiveCTA,
        props.itemFiveLink,
        props.itemTitleAlign,
        theme.widget.categorySet.sixItemCategory.contentGutterPadding,
      ),
      buildSixItemCategory(
        props.itemSixTitle,
        props.pictures.pictureSix,
        props.itemSixCTA,
        props.itemSixLink,
        props.itemTitleAlign,
        theme.widget.categorySet.sixItemCategory.contentGutterPadding,
      ),
    ],
  }

  return <StyledCategorySet {...categorySetProps} />
}

export const CategorySetWidgetRenderer = (
  props: CategorySetWidgetRendererProps,
) => {
  const theme = useTheme()
  const { enableStyleOverrideSixItemCategoryWidget } = useSiteConfig()

  if (props.__typename === 'GlobalSixItemCategories') {
    return renderGlobalSixItemCategories(
      props,
      theme,
      enableStyleOverrideSixItemCategoryWidget,
    )
  }
  if (
    props.__typename === 'multiCategoryCardSet' &&
    props.banners.length >= 2
  ) {
    const categoryItemsMap = props.banners.map((item) => {
      return {
        ...(item.itemTitle && {
          titleProps: {
            title: item.itemTitle,
          },
          font: {
            style: theme.widget.categorySet.multiCategory.item.title.font.style,
          },
        }),
        content: item.itemDescription,
        horizontalAlignment: validAlignment(item.textAlignment),
        picture: item.picture,
        buttonContent: item.itemCTA || undefined,
        buttonLink: item.itemUrl || undefined,
        contentGutterPadding:
          theme.widget.categorySet.multiCategory.contentGutterPadding,
        emphasis:
          theme.widget.categorySet.multiCategory.item.cta.emphasis || 'low',
        ctaPadding: theme.widget.categorySet.multiCategory.item.cta.padding,
        anchorWidth:
          theme.widget.categorySet.multiCategory?.anchorWidth || 'auto',
      } as CategorySetItemProps
    })
    const categorySetProps: CategorySetProps = {
      horizontalAlignment: 'center',
      gutterPadding: theme.widget.categorySet.multiCategory.gutterPadding,
      container: theme.widget.categorySet.multiCategory.container,
      gutterMargin: theme.widget.categorySet.multiCategory.gutterMargin,
      spacingMultiplier:
        theme.widget.categorySet.multiCategory.spacingMultiplier,
      items: [
        categoryItemsMap[0],
        categoryItemsMap[1],
        ...categoryItemsMap.splice(2),
      ],
    }

    return (
      <WidgetBackgroundColour backgroundColour={props.backgroundColour}>
        <StyledCategorySet
          {...categorySetProps}
          titleProps={
            props.widgetTitle ? { title: props.widgetTitle } : undefined
          }
          subtitleProps={
            props.widgetSubtitle
              ? { subtitle: props.widgetSubtitle }
              : undefined
          }
          numberOfColumns={props.columnsNumber || undefined}
        />
      </WidgetBackgroundColour>
    )
  }
  if (props.__typename === 'multiCategoryCardSetV2') {
    const titleTheme = props.theme
    const items: CategorySetItemProps[] = props?.banners?.map((banner) => ({
      ...(banner.itemTitle && {
        titleProps: {
          title: banner.itemTitle,
          titleColour:
            titleTheme === 'light' || titleTheme === 'dark'
              ? titleTheme
              : undefined,
        },
      }),
      picture: {
        sources: [
          {
            sourceSet: banner.itemImagePath,
          },
        ],
        default: banner.itemImagePath,
        alt: banner.altTextitem!,
      },
      horizontalAlignment: validAlignment(banner.textAlignment),
      contentGutterPadding:
        theme.widget.categorySet.multiCategoryCardSetV2.contentGutterPadding,
      buttonLink: banner.itemUrl || undefined,
      font: theme.widget.categorySet.multiCategoryCardSetV2.font,
    }))

    const categorySetProps: CategorySetProps = {
      items: [items[0], items[1], ...items.slice(2)],
      ...(props.widgetTitle && {
        titleProps: {
          title: props.widgetTitle,
          titleColour:
            titleTheme === 'light' || titleTheme === 'dark'
              ? titleTheme
              : undefined,
        },
      }),
      cardStyle: props.alternativeItemCardDesign || undefined,
      margin: props.marginStyle,
      gutterPadding:
        theme.widget.categorySet.multiCategoryCardSetV2.gutterPadding,
      container: theme.widget.categorySet.multiCategoryCardSetV2.container,
      gutterMargin:
        theme.widget.categorySet.multiCategoryCardSetV2.gutterMargin,
      styleOverride:
        theme.widget.categorySet.multiCategoryCardSetV2.flexSpacing !==
        undefined
          ? {
              margin:
                theme.widget.categorySet.multiCategoryCardSetV2.flexSpacing,
              width: theme.widget.categorySet.multiCategoryCardSetV2.width,
            }
          : undefined,
    }
    return (
      <WidgetBackgroundColour
        backgroundColour={props.backgroundColour || undefined}
        margin={categorySetProps.margin}
      >
        <StyledCategorySet
          {...categorySetProps}
          numberOfColumns={props.columnsNumber || undefined}
          mobileColumns={props.mobileColumns || undefined}
        />
      </WidgetBackgroundColour>
    )
  }
  return null
}
